import { useEffect, useRef, useState } from "react";
import {
  IoChevronBack,
  IoChevronForward,
  IoArrowForwardCircleOutline,
} from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router";

const HowItWorks = () => {

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const paddedRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);
  const autoScrollTimerRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const isResettingRef = useRef(false);
  const { t } = useTranslation();

  const handleButtonLearnMore = () => {
    navigate('/get-started');
  };

  const originalSteps = [
    {
      stepNumber: 1,
      title: t('howItWorks.step1Title'),
      subtitle: t('howItWorks.step1Subtitle'),
      description:
        t('howItWorks.step1Description'),
      image: "/assets/Subtract.webp",
    },
    {
      stepNumber: 2,
      title: t('howItWorks.step2Title'),
      subtitle: t('howItWorks.step2Subtitle'),
      description:
        t('howItWorks.step2Description'),
      image: "/assets/2.webp",
    },
    {
      stepNumber: 3,
      title: t('howItWorks.step3Title'),
      subtitle: t('howItWorks.step3Subtitle'),
      description:
        t('howItWorks.step3Description'),
      image: "/assets/3.webp",
    },
    {
      stepNumber: 4,
      title: t('howItWorks.step4Title'),
      subtitle: t('howItWorks.step4Subtitle'),
      description:
        t('howItWorks.step4Description'),
      image: "/assets/4.webp",
    },
  ];

  // Create infinite loop by duplicating slides
  const steps = [...originalSteps, ...originalSteps, ...originalSteps];

  // Initialize to middle set for seamless infinite loop
  useEffect(() => {
    setCurrentStep(originalSteps.length); // Start at the beginning of the middle set
    isResettingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll functionality with seamless infinite loop
  useEffect(() => {
    if (!isAutoScrollEnabled) return;

    autoScrollTimerRef.current = setInterval(() => {
      setCurrentStep((prev) => prev + 1);
    }, 4000); // Auto-scroll every 4 seconds

    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
    };
  }, [isAutoScrollEnabled]);

  // Seamless loop reset - creates infinite scroll effect
  useEffect(() => {
    if (isResettingRef.current) return;

    // If we've reached the beginning of the last set, instantly reset to middle set
    if (currentStep >= originalSteps.length * 2) {
      isResettingRef.current = true;
      setTimeout(() => {
        setIsTransitioning(false);
        setTimeout(() => {
          setCurrentStep(originalSteps.length);
          setTimeout(() => {
            setIsTransitioning(true);
            isResettingRef.current = false;
          }, 50);
        }, 50);
      }, 500); // Wait for current transition to complete
    }
    // If we've gone into the first set, instantly reset to equivalent position in middle set
    else if (currentStep < originalSteps.length) {
      isResettingRef.current = true;
      setTimeout(() => {
        setIsTransitioning(false);
        setTimeout(() => {
          setCurrentStep(currentStep + originalSteps.length);
          setTimeout(() => {
            setIsTransitioning(true);
            isResettingRef.current = false;
          }, 50);
        }, 50);
      }, 500);
    }
  }, [currentStep, originalSteps.length]);

  // Handle manual navigation with auto-scroll pause
  const handleManualNavigation = (direction) => {
    // Pause auto-scroll when user manually navigates
    setIsAutoScrollEnabled(false);
    
    if (direction === 'next') {
      setCurrentStep((prev) => prev + 1);
    } else {
      setCurrentStep((prev) => prev - 1);
    }

    // Clear any existing pause timer
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
    }

    // Resume auto-scroll after 8 seconds of inactivity
    pauseTimerRef.current = setTimeout(() => {
      setIsAutoScrollEnabled(true);
    }, 8000);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
      }
    };
  }, []);

  // Center the active card in view
  useEffect(() => {
    const centerActive = () => {
      const containerEl = containerRef.current;
      const paddedEl = paddedRef.current; // element that has left padding
      const trackEl = trackRef.current;
      if (!containerEl || !trackEl) return;
      const children = trackEl.children;
      if (!children || !children[currentStep]) return;
      const active = children[currentStep];
      const containerWidth = (paddedEl || containerEl).clientWidth;
      const activeCenter = active.offsetLeft + active.clientWidth / 2;
      // For the first slide, align to the very left (no left space)
      if (currentStep === 0) {
        setTranslateX(0);
        return;
      }
      const styles = window.getComputedStyle(paddedEl || containerEl);
      const paddingLeft = parseFloat(styles.paddingLeft || "0");
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      if (!isDesktop) {
        // Mobile: show one full slide; align its left edge to container
        const leftAligned = Math.max(0, active.offsetLeft - paddingLeft);
        setTranslateX(leftAligned);
        return;
      }
      // Desktop/tablet: center the active card, accounting for padding
      const visibleWidth = containerWidth - paddingLeft;
      const rawScroll = activeCenter - visibleWidth / 5 - paddingLeft;
      const maxScroll = Math.max(0, trackEl.scrollWidth - visibleWidth);
      const targetScroll = Math.min(Math.max(0, rawScroll), maxScroll);
      setTranslateX(targetScroll);
    };

    centerActive();
    window.addEventListener("resize", centerActive);
    return () => window.removeEventListener("resize", centerActive);
  }, [currentStep]);

  return (
    <section
      className="relative py-16 md:py-20 lg:py-24 bg-cover bg-center bg-no-repeat overflow-x-hidden"
      style={{
        backgroundImage: "url('/assets/how-bg.png')",
      }}
    >
      <div className="container-custom mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="space-y-4">
            <div className="flex bg-white px-4 py-2 rounded-full w-fit">
              <p className="text-[#5A5A59] text-sm font-medium">{t('howItWorks.buttons')}</p>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#001F3D] leading-tight max-w-lg">
              {t('howItWorks.step5Title')}
            </h2>
          </div>

          <div className="space-y-6">
            <p className="text-base sm:text-lg font-normal text-[#5A5A59] leading-relaxed max-w-sm">
              {t('howItWorks.step5Description')}
            </p>

            <button className="flex items-center gap-x-2 btn-primary" onClick={handleButtonLearnMore}>
              {t('howItWorks.button')}
              <IoArrowForwardCircleOutline className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div className="relative overflow-hidden" ref={containerRef}>
          <div className="overflow-hidden pl-4 md:pl-8 lg:pl-60" ref={paddedRef}>
            <div
              className={`flex gap-6 md:gap-8 lg:gap-10 ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
              ref={trackRef}
              style={{
                transform: `translateX(-${translateX}px)`,
              }}
            >
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0.9, opacity: 0.7 }}
                  animate={{
                    scale: index === currentStep ? 1 : 0.9,
                    opacity: index === currentStep ? 1 : 0.7,
                  }}
                  transition={{
                    duration: 0.5,
                    ease: "easeInOut",
                  }}
                  className="w-full md:w-[60%] lg:w-[70%] flex-shrink-0"
                >
                  <div className="relative rounded-2xl overflow-visible">
                    <div
                      className={`relative w-full rounded-2xl bg-cover bg-center bg-no-repeat transition-transform duration-500 ease-in-out ${
                        index === currentStep
                          ? "h-[320px] md:h-[400px] lg:h-[480px]"
                          : "h-[280px] md:h-[360px] lg:h-[440px]"
                      }`}
                      style={{
                        backgroundImage: `url('${step.image}')`,
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {index === currentStep && (
                          <>
                            <motion.button
                              key={`desktop-prev-${index}`}
                              onClick={() => handleManualNavigation('prev')}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ duration: 0.2 }}
                              className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 md:h-11 md:w-11 hidden md:flex items-center justify-center rounded-full bg-white text-black hover:bg-gray-100 transition-colors shadow-lg"
                              aria-label="Previous step"
                            >
                              <IoChevronBack className="w-5 h-5" />
                            </motion.button>

                            <motion.button
                              key={`desktop-next-${index}`}
                              onClick={() => handleManualNavigation('next')}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ duration: 0.2 }}
                              className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 md:h-11 md:w-11 hidden md:flex items-center justify-center rounded-full bg-[#14C5D1] text-white hover:bg-[#0D7780] transition-colors shadow-lg"
                              aria-label="Next step"
                            >
                              <IoChevronForward className="w-5 h-5" />
                            </motion.button>
                          </>
                        )}
                      </AnimatePresence>
                      <motion.div 
                        className="absolute bottom-0 left-0 p-3 md:p-4 lg:p-6 z-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <motion.p 
                          className="text-white text-sm md:text-base font-light mb-1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.3 }}
                        >
                        {t('howItWorks.step')} {step.stepNumber}
                        </motion.p>
                        <motion.h3 
                          className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-1"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.4 }}
                        >
                          {step.title}
                        </motion.h3>
                        <motion.h3 
                          className="text-white text-2xl md:text-3xl lg:text-4xl font-bold leading-tight"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.5 }}
                        >
                          {step.subtitle}
                        </motion.h3>
                      </motion.div>

                      <AnimatePresence mode="wait">
                        {index === currentStep && (
                          <motion.div 
                            key={`desc-${index}`}
                            className="hidden md:block absolute bottom-0 right-0 w-full md:w-[50%] lg:w-[45%] z-20 overflow-visible"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                          >
                            <div className="relative overflow-visible">
                              <div className="relative w-full h-full min-h-[200px] overflow-visible">
                                <div className="absolute top-[45%] md:top-[50%] lg:top-[55%] left-0 right-0 pl-8 pr-5 md:pl-10 md:pr-6 lg:pl-14 lg:pr-10 pb-5 md:pb-7 lg:pb-8 z-30 mt-5">
                                  <motion.p 
                                    className="text-[#5A5A59] text-xs md:text-sm lg:text-[10px] leading-relaxed max-w-xs"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.5 }}
                                  >
                                    {step.description}
                                  </motion.p>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {index === currentStep && (
                      <motion.div 
                        key={`mobile-desc-${index}`}
                        className="md:hidden mt-4 px-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p className="text-[#5A5A59] text-sm leading-relaxed">
                          {step.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Mobile-only navigation buttons under the card for active slide */}
                  <AnimatePresence mode="wait">
                    {index === currentStep && (
                      <motion.div 
                        key={`mobile-nav-${index}`}
                        className="md:hidden flex items-center justify-center gap-6 mt-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.button
                          onClick={() => handleManualNavigation('prev')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="h-12 w-12 flex items-center justify-center rounded-full bg-white text-black shadow-md"
                          aria-label="Previous step"
                        >
                          <IoChevronBack className="w-5 h-5" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleManualNavigation('next')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="h-12 w-12 flex items-center justify-center rounded-full bg-[#14C5D1] text-white shadow-md"
                          aria-label="Next step"
                        >
                          <IoChevronForward className="w-5 h-5" />
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
